import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import useDirectory from 'hooks/useDirectory'
import { useRecoilState } from 'recoil'
import { floorDetailState } from 'store/buildingStore'
import useSWR from 'swr'
import { FloorDetail, FloorMapElement } from 'types/building'
import Utils from 'utils'

/**
 * useKrpanoFloor Hook
 * Centralizes floor detail and points querying, Recoil state synchronization,
 * and loading state coordination for the KrPano domain.
 */
export function useKrpanoFloor(
  floorId: string | number | undefined,
  buildingIdParam?: string | number | undefined
) {
  const { id: routeBuildingId } = useParams()
  const buildingId = buildingIdParam || routeBuildingId
  const [floorDetail, setFloorDetail] = useRecoilState(floorDetailState)
  const { navigate } = useDirectory()

  const {
    data: floorData,
    isValidating: isValidatingFloor,
    isLoading: isLoadingFloor,
    mutate: mutateFloor
  } = useSWR<FloorDetail>(floorId ? { url: `v1/floors/${floorId}` } : null, {
    onError: (err) => {
      Utils.handleErrorNavigate(err, navigate)
    }
  })

  const {
    data: pointsData,
    isValidating: isValidatingPoints,
    isLoading: isLoadingPoints,
    mutate: mutatePoints
  } = useSWR<FloorMapElement>(
    floorId && buildingId
      ? {
          url: 'v1/points',
          params: {
            buildingId,
            floorId
          }
        }
      : null
  )

  // Synchronously prioritize active floor data matching the current floorId
  const currentFloorDetail = useMemo(() => {
    if (floorData && floorId && floorData.id === Number(floorId)) {
      return floorData
    }
    return floorData || floorDetail
  }, [floorData, floorDetail, floorId])

  useEffect(() => {
    if (floorData) {
      setFloorDetail(floorData)
    }
  }, [floorData, setFloorDetail])

  const imagePath = useMemo(() => {
    if (currentFloorDetail?.isUnregisteredMap && !currentFloorDetail?.mapFile) {
      return '/assets/white-default.png'
    }
    if (currentFloorDetail?.mapFile && !currentFloorDetail?.isUnregisteredMap) {
      return currentFloorDetail.mapFile.s3Path
    }
    return ''
  }, [currentFloorDetail?.isUnregisteredMap, currentFloorDetail?.mapFile])

  const isFloorLoading =
    isValidatingFloor ||
    isValidatingPoints ||
    isLoadingFloor ||
    isLoadingPoints ||
    (!floorData && Boolean(floorId))

  return {
    floorDetail: currentFloorDetail,
    setFloorDetail,
    imagePath,
    pointsData,
    isFloorLoading,
    isValidating: isFloorLoading,
    isValidatingFloor,
    isValidatingPoints,
    isLoading: isFloorLoading,
    mutate: mutateFloor,
    mutatePoints
  }
}

export default useKrpanoFloor
